import { clearCache, getCache, setCache } from '@/lib/utils';
import { supabase } from './supabase';

// Cache keys
const ENTITIES_CACHE_PREFIX = 'entities_';
const ENTITY_CACHE_PREFIX = 'entity_';
const STORY_ENTITIES_CACHE_PREFIX = 'story_entities_';
const PROJECT_ENTITIES_CACHE_PREFIX = 'project_entities_';

// Cache expiration times (in milliseconds)
const CACHE_SHORT = 2 * 60 * 1000; // 2 minutes
const CACHE_MEDIUM = 5 * 60 * 1000; // 5 minutes
const CACHE_LONG = 15 * 60 * 1000; // 15 minutes

/**
 * Entity interface
 */
export interface Entity {
  id: number;
  name: string;
  description: string | null;
  status: string;
  template_id: number;
  category_id: number;
  created_at: string;
  updated_at: string | null;
  template_name?: string;
  template_type?: string;
  category_name?: string;
  project_id?: number | string;
  field_values?: Record<string, any>;
}

/**
 * Story Entity relationship interface
 */
export interface StoryEntity {
  id: number;
  story_id: number;
  entity_id: number;
  role: string;
  created_at: string;
  entity?: Entity;
}

/**
 * Get all entities for a project
 * @param projectId - Project ID
 * @returns Promise with array of entities
 */
export const getProjectEntities = async (projectId: string): Promise<Entity[]> => {
  // Try to get from cache first
  const cacheKey = `${PROJECT_ENTITIES_CACHE_PREFIX}${projectId}`;
  const cachedEntities = getCache<Entity[]>(cacheKey);

  if (cachedEntities) {
    return cachedEntities;
  }

  // If not in cache, fetch from API
  const { data, error } = await supabase
    .from('entities')
    .select(`
      *,
      templates:template_id (
        name
      ),
      template_categories:category_id (
        name
      )
    `)
    .eq('project_id', projectId)
    .order('name');

  if (error) {
    console.error('Error fetching project entities:', error);
    throw error;
  }

  // Transform the data to match the Entity interface
  const entities: Entity[] = data.map(item => ({
    ...item,
    template_name: item.templates?.name,
    template_type: 'Universal', // Since templates are no longer associated with categories
    category_name: item.template_categories?.name
  }));

  // Cache the result
  setCache(cacheKey, entities, CACHE_MEDIUM);

  return entities;
};

/**
 * Get entities for a story
 * @param storyId - Story ID
 * @returns Promise with array of story entities
 */
export const getStoryEntities = async (storyId: string): Promise<StoryEntity[]> => {
  // Try to get from cache first
  const cacheKey = `${STORY_ENTITIES_CACHE_PREFIX}${storyId}`;
  const cachedStoryEntities = getCache<StoryEntity[]>(cacheKey);

  if (cachedStoryEntities) {
    return cachedStoryEntities;
  }

  // If not in cache, fetch from API
  const { data, error } = await supabase
    .from('story_entities')
    .select(`
      *,
      entity:entity_id (
        *,
        templates:template_id (
          name
        ),
        template_categories:category_id (
          name
        )
      )
    `)
    .eq('story_id', storyId)
    .order('created_at');

  if (error) {
    console.error('Error fetching story entities:', error);
    throw error;
  }

  // Transform the data to match the StoryEntity interface
  const storyEntities: StoryEntity[] = data.map(item => ({
    ...item,
    entity: item.entity ? {
      ...item.entity,
      template_name: item.entity.templates?.name,
      template_type: 'Universal', // Since templates are no longer associated with categories
      category_name: item.entity.template_categories?.name
    } : undefined
  }));

  // Cache the result
  setCache(cacheKey, storyEntities, CACHE_MEDIUM);

  return storyEntities;
};

/**
 * Get entity by ID
 * @param entityId - Entity ID
 * @returns Promise with entity details
 */
export const getEntityById = async (entityId: string | number): Promise<Entity> => {
  // Try to get from cache first
  const cacheKey = `${ENTITY_CACHE_PREFIX}${entityId}`;
  const cachedEntity = getCache<Entity>(cacheKey);

  if (cachedEntity) {
    return cachedEntity;
  }

  // If not in cache, fetch from API
  const { data, error } = await supabase
    .from('entities')
    .select(`
      *,
      templates:template_id (
        name
      ),
      template_categories:category_id (
        name
      )
    `)
    .eq('id', entityId)
    .single();

  if (error) {
    console.error('Error fetching entity:', error);

    // Handle specific error cases
    if (error.code === 'PGRST116') {
      console.warn(`Entity with ID ${entityId} not found or access denied`);
      // Return null instead of throwing for "no rows returned" error
      return null as unknown as Entity;
    }

    throw error;
  }

  // Transform the data to match the Entity interface
  const entity: Entity = {
    ...data,
    template_name: data.templates?.name,
    template_type: 'Universal', // Since templates are no longer associated with categories
    category_name: data.template_categories?.name
  };

  // Cache the result
  setCache(cacheKey, entity, CACHE_MEDIUM);

  return entity;
};

/**
 * Create a new entity
 * @param projectId - Project ID
 * @param data - Entity data
 * @returns Promise with created entity
 */
export const createEntity = async (
  projectId: string,
  data: {
    name: string;
    description?: string;
    status?: string;
    template_id: number;
    category_id: number;
    field_values?: Record<string, any>;
  }
): Promise<Entity> => {
  const { data: entity, error } = await supabase
    .from('entities')
    .insert([
      {
        project_id: projectId,
        name: data.name,
        description: data.description || null,
        status: data.status || 'active',
        template_id: data.template_id,
        category_id: data.category_id,
        field_values: data.field_values || {},
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating entity:', error);
    throw error;
  }

  // Clear project entities cache
  clearCache(`${PROJECT_ENTITIES_CACHE_PREFIX}${projectId}`);

  return entity;
};

/**
 * Update an entity
 * @param entityId - Entity ID
 * @param data - Updated entity data
 * @returns Promise with updated entity
 */
export const updateEntity = async (
  entityId: string | number,
  data: Partial<Entity>
): Promise<Entity> => {
  const { data: entity, error } = await supabase
    .from('entities')
    .update(data)
    .eq('id', entityId)
    .select()
    .single();

  if (error) {
    console.error('Error updating entity:', error);
    throw error;
  }

  // Clear entity cache
  clearCache(`${ENTITY_CACHE_PREFIX}${entityId}`);

  // Clear project entities cache (we don't know the project ID here)
  // This will be handled by cache expiration

  return entity;
};

/**
 * Delete an entity
 * @param entityId - Entity ID
 * @param projectId - Project ID (optional, for cache clearing)
 */
export const deleteEntity = async (entityId: string | number, projectId?: string | number): Promise<void> => {
  const { error } = await supabase
    .from('entities')
    .delete()
    .eq('id', entityId);

  if (error) {
    console.error('Error deleting entity:', error);
    throw error;
  }

  // Clear entity cache
  clearCache(`${ENTITY_CACHE_PREFIX}${entityId}`);

  // Clear project entities cache if project ID is provided
  if (projectId) {
    clearCache(`${PROJECT_ENTITIES_CACHE_PREFIX}${projectId}`);
  }
};

/**
 * Add entity to story
 * @param storyId - Story ID
 * @param entityId - Entity ID
 * @param role - Entity role in the story
 * @returns Promise with created story entity
 */
export const addEntityToStory = async (
  storyId: string | number,
  entityId: string | number,
  role: string = 'supporting'
): Promise<StoryEntity> => {
  const { data, error } = await supabase
    .from('story_entities')
    .insert([
      {
        story_id: storyId,
        entity_id: entityId,
        role: role,
        // tenant_id is automatically set by RLS policies using auth.jwt()
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error adding entity to story:', error);
    throw error;
  }

  // Clear story entities cache
  clearCache(`${STORY_ENTITIES_CACHE_PREFIX}${storyId}`);

  return data;
};

/**
 * Remove entity from story
 * @param storyEntityId - Story entity relationship ID
 * @param storyId - Story ID (for cache clearing)
 */
export const removeEntityFromStory = async (
  storyEntityId: string | number,
  storyId: string | number
): Promise<void> => {
  const { error } = await supabase
    .from('story_entities')
    .delete()
    .eq('id', storyEntityId);

  if (error) {
    console.error('Error removing entity from story:', error);
    throw error;
  }

  // Clear story entities cache
  clearCache(`${STORY_ENTITIES_CACHE_PREFIX}${storyId}`);
};

/**
 * Update entity role in story
 * @param storyEntityId - Story entity relationship ID
 * @param role - New role
 * @param storyId - Story ID (for cache clearing)
 * @returns Promise with updated story entity
 */
export const updateEntityRole = async (
  storyEntityId: string | number,
  role: string,
  storyId: string | number
): Promise<StoryEntity> => {
  const { data, error } = await supabase
    .from('story_entities')
    .update({ role })
    .eq('id', storyEntityId)
    .select()
    .single();

  if (error) {
    console.error('Error updating entity role:', error);
    throw error;
  }

  // Clear story entities cache
  clearCache(`${STORY_ENTITIES_CACHE_PREFIX}${storyId}`);

  return data;
};